import { useRollbar } from '@rollbar/react'
import { QueryClientProvider } from '@tanstack/react-query'
import axios from 'axios'
import {
  fetchColorsSuccess,
  fetchPageDataFailure,
  fetchPageDataSuccess,
  fetchWebsitePageDataSuccess,
} from 'client/actions/pageActions'
import {
  fetchPageAutomationRulesData,
  fetchPageData,
  fetchUserColors,
} from 'client/api/pageApi'
import CodeDialog from 'client/components/core/CodeEditor/CodeEditorDialog'
import CommonErrorsDialog from 'client/components/core/ErrorsDialog/CommonErrorsDialog'
import ValidationErrorsDialog from 'client/components/core/ErrorsDialog/ValidationErrorsDialog'
import Toast from 'client/components/core/Toast'
import useBeforeunload from 'client/hooks/useBeforeunload'
import usePageRouteDetector from 'client/hooks/usePageRouteDetector'
import { useWebsitePages } from 'client/store'
import { fetchPageAutomationRules } from 'client/store/automationRules/automationRulesActions'
import { migratePage } from 'client/utils/migratePage'
import { AccessDenied } from 'common/errors/AccessDenied'
import { NetworkError } from 'common/errors/NetworkError'
import { NotFound } from 'common/errors/NotFound'
import { useActions } from 'common/hooks/useActions'
import React, { memo, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { useDispatch } from 'react-redux'
import { useApplyUserLocale } from 'src/editor/hooks/use-apply-user-locale'
import { useSentReduxLogsToRollbar } from 'src/editor/hooks/use-sent-redux-logs-to-rollbar'
import { useWebsitePagesContent } from 'src/editor/hooks/use-website-pages-content'
import { Layout } from 'src/editor/layout'
import { Content } from 'src/editor/layout/content'
import { WebsiteContent } from 'src/editor/layout/website-content'
import { GetEntityCustomNameProvider } from 'src/editor/shared/context/create-get-entity-custom-name-context'
import { GrowthbookClientProvider } from 'src/editor/shared/growthbook/growthbook-provider'
import { addFavicon } from 'src/editor/shared/utils/add-favicon'
import { queryClient } from 'src/editor/shared/utils/queryClient'
import { fetchWebsitePageData } from 'src/editor/store/website-pages/website-pages-actions'
import { getIsPreviewMode } from 'src/editor/store/website-pages/website-pages-selector'
import { isCustomDomain } from 'src/shared/utils/is-custom-domain'
import addSectionToWebsitePageCommand from 'tools/commands/add-section-to-website-page-command'
import * as managementActions from './actions/actionsManagement'
import { DeviceModeProvider } from './context/DeviceModeContext'
import { HighlightedEntityProvider } from './context/HighlightedContext'
import SidebarVisibilityContextProvider from './context/SidebarVisibilityContext'
import { locales } from './i18n'

const AppNew = () => {
  if (process.env.NODE_ENV !== 'development') {
    useBeforeunload(e => e.preventDefault())
  }
  const { i18n } = useTranslation()
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const dispatchFetchPageData = useActions(fetchPageDataSuccess)
  const dispatchFetchWebsitePageData = useActions(fetchWebsitePageDataSuccess)
  const dispatchFetchWebsitePage = useActions(fetchWebsitePageData)
  const dispatchFetchColors = useActions(fetchColorsSuccess)
  const dispatch = useDispatch()
  const isWebsitePreview = useWebsitePages(getIsPreviewMode)
  const isWlUser = isCustomDomain()
  const rollbar = useRollbar()
  const dispatchFetchPageDataFail = useActions(fetchPageDataFailure)
  const setHasLostEntities = useActions(managementActions.setHasLostEntities)
  const setHasMasterBlockRootWithoutMasterBlockId = useActions(
    managementActions.setHasMasterBlockRootWithoutMasterBlockId,
  )
  const setHasMasterBlockIdsWithoutMasterRoot = useActions(
    managementActions.setHasMasterBlockIdsWithoutMasterRoot,
  )
  const { pageId, isTemplate, isWebsite } = usePageRouteDetector()
  const { isUserFetched, isLocaleReady, user } = useApplyUserLocale()
  const { getWebsitePageContent } = useWebsitePagesContent()

  useSentReduxLogsToRollbar()

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const browserLanguage = window.navigator.languages.find(lng =>
      locales.includes(lng),
    )
    const fixedT = i18n.getFixedT(browserLanguage)

    if (!isWlUser) {
      addFavicon(`${process.env.EDITOR_ASSETS_HOST}/assets/images/favicon.ico`)
    }

    const fetchPage = async pageId => {
      setIsLoading(true)
      try {
        if (isWebsite) {
          const { pageContent, websiteData, layoutPageId, isFromCache } =
            await getWebsitePageContent(pageId)
          const userColors = await fetchUserColors()
          if (!isFromCache && websiteData) {
            dispatchFetchWebsitePage({
              ...websiteData,
              layoutPageId,
            })
          }
          try {
            // NOTE: add section to website page command for https://app.shortcut.com/systeme/story/86891/support-section-entity-for-website-pages-websitepagebody
            pageContent.page = addSectionToWebsitePageCommand(
              pageContent.page,
            )[0]
          } catch (error) {
            rollbar.error(error)
          }
          dispatchFetchWebsitePageData(pageContent, user, rollbar)
          dispatchFetchColors(userColors.data)
        } else {
          const response = await fetchPageData(pageId, isTemplate)
          try {
            response.data.page = migratePage(response.data.page)
          } catch (error) {
            rollbar.error(error)
          }
          dispatchFetchPageData(response.data, rollbar)
        }
        setIsLoading(false)
      } catch (error) {
        // it's a way to show to QA and devs that the page is broken
        if (
          process.env.APP_ENV === 'staging' &&
          axios.isAxiosError(error) &&
          error.response.status === 400
        ) {
          toast.error(error.response.data.message, { duration: 2000 })
          return
        }

        // the code below won't work since no axios interceptor was applied
        // todo we need to handle 500 and 401
        if (error instanceof AccessDenied) {
          // do nothing
        } else if (error instanceof NotFound) {
          dispatchFetchPageDataFail(fixedT('core.errors.page_not_found'))
        } else if (error instanceof NetworkError) {
          dispatchFetchPageDataFail(fixedT('core.errors.no_connection'))
        } else {
          dispatchFetchPageDataFail(
            fixedT('core.errors.went_wrong_contact_support'),
          )
          rollbar.error(error)
          setIsError(true)
        }
      }
    }

    const fetchAutomationRules = async pageId => {
      try {
        const response = await fetchPageAutomationRulesData(pageId, isTemplate)
        dispatch(fetchPageAutomationRules(response.data))
        // biome-ignore lint/correctness/noUnusedVariables: error intentionally ignored
      } catch (error) {
        // do nothing
      }
    }

    if (!pageId) {
      dispatchFetchPageDataFail(fixedT('core.errors.page_not_found'))
    } else {
      if (!isUserFetched || !user) {
        return
      }

      fetchPage(pageId)
      if (!isTemplate) {
        fetchAutomationRules(pageId)
      }
    }
  }, [
    dispatch,
    dispatchFetchPageData,
    dispatchFetchPageDataFail,
    i18n,
    isTemplate,
    setHasLostEntities,
    setHasMasterBlockIdsWithoutMasterRoot,
    setHasMasterBlockRootWithoutMasterBlockId,
    isUserFetched,
    user,
  ])

  const loading = isLoading || !isUserFetched || !isLocaleReady

  return (
    <QueryClientProvider client={queryClient}>
      <GrowthbookClientProvider>
        <SkeletonTheme color="#F2F2F380" highlightColor="#F2F2F3">
          <SidebarVisibilityContextProvider>
            <DeviceModeProvider>
              <HighlightedEntityProvider>
                <GetEntityCustomNameProvider>
                  <Layout isWebsitePreview={isWebsitePreview}>
                    {isWebsitePreview ? (
                      <WebsiteContent />
                    ) : (
                      !isError && <Content isLoading={loading} />
                    )}
                    <ValidationErrorsDialog />
                    <CommonErrorsDialog />
                    <CodeDialog />
                    <Toast />
                  </Layout>
                </GetEntityCustomNameProvider>
              </HighlightedEntityProvider>
            </DeviceModeProvider>
          </SidebarVisibilityContextProvider>
        </SkeletonTheme>
      </GrowthbookClientProvider>
    </QueryClientProvider>
  )
}

export default memo(AppNew)
